'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { AUTH_CONFIG } from '@eliadmin/core/src/constants/auth-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
export const signInSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
};

export function SignInForm({ onSuccess, initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      const resp = await signIn({ ...data, locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn(
          'ea-sign-in-form',
          'flex w-full flex-col items-center justify-center gap-6 p-6 sm:gap-8 sm:p-8 md:p-8 lg:gap-10 lg:p-10 xl:gap-12 xl:p-12',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6">
          <h4 className="w-full text-center text-3xl tracking-tighter sm:text-4xl">
            {t('text190')}
          </h4>
          <p className="w-full text-center text-sm sm:text-base">
            {t('text193')}{' '}
            {inDialog ? (
              <DialogClose asChild>
                <Link
                  prefetch={false}
                  href={ROUTES.REGISTER()}
                  className="font-medium text-primary"
                >
                  {t('text2019')}
                </Link>
              </DialogClose>
            ) : (
              <Link prefetch={false} href={ROUTES.REGISTER()} className="font-medium text-primary">
                {t('text2019')}
              </Link>
            )}
          </p>
        </div>
        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div
          className={cn('flex w-full flex-col gap-4 sm:gap-5 lg:gap-6', 'ea-sign-in-form__content')}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-sign-in-form__email-field')}>
                <FormControl className={cn('', 'ea-sign-in-form__email-input')}>
                  <Input {...field} placeholder={t('text259')} />
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__email-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-sign-in-form__password-field')}>
                <FormControl className={cn('', 'ea-sign-in-form__password-input')}>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      type={showPassword ? 'text' : 'password'}
                      after={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onMouseDown={handlePasswordMouseDown}
                          onMouseUp={handlePasswordMouseUp}
                          onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                          onTouchStart={handlePasswordMouseDown}
                          onTouchEnd={handlePasswordMouseUp}
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      }
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* FOOTER */}
        <div className={cn('flex flex-col items-start space-y-4', 'ea-sign-in-form__footer')}>
          {/* <div
              className={cn(
                'flex flex-row items-center space-x-2',
                'ea-sign-in-form__checkbox-wrapper',
              )}
            >
              <Checkbox id="remember-me" className={cn('', 'ea-sign-in-form__checkbox')} />
              <Label htmlFor="remember-me" className={cn('', 'ea-sign-in-form__checkbox-label')}>
                {t('text260')}
              </Label>
            </div> */}
          <Button
            type="submit"
            className={cn('h-12 w-full text-lg', 'ea-sign-in-form__submit-button')}
            variant={'dark'}
            isLoading={form.formState.isSubmitting}
          >
            {t('text189')}
          </Button>
          {AUTH_CONFIG.signIn.passwordResetPosition === 'inside' && (
            <div
              className={cn('mx-auto mt-12 flex justify-center', 'ea_sign-in-form__password-reset')}
            >
              <Link
                className={cn(
                  'w-full text-center text-sm font-medium text-primary underline',
                  'ea_sign-in-form__password-reset-link ',
                )}
                href={ROUTES.PASSWORD_RESET() ?? '#'}
                prefetch={false}
              >
                {t('text188')}
              </Link>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
